import {AxiosProgressEvent, AxiosResponse} from "axios"
import {TCompanyId, IRawFile, IStandardResponse} from "../types";
import {IRawFileDTO} from "../types/dtos/IRawFileDTO"
import {IUploadFileRequestDTO} from "../types/dtos/IUploadFileRequestDTO";
import {apiClient, RetryConfig, UPLOAD_TIMEOUT_MS} from "./apiClient"
import {IUploadFileRequestResponseDTO} from "../types/dtos/IUploadFileRequestResponseDTO";
import {IFileUploadStatus} from "@/protocols/types/protocol.ts";
import {FileWithPath} from "@mantine/dropzone";

export const requestRawFileUpload = async (
    companyId: TCompanyId,
    _file: FileWithPath,
    metadata: IUploadFileRequestDTO,
    config?: RetryConfig,
): Promise<IFileUploadStatus> => {
    const url = `/companies/${companyId}/raw_files/ur`
    const res: AxiosResponse<IStandardResponse<IUploadFileRequestResponseDTO>> = await apiClient.post(url, metadata, config)
    const dto = res.data.data

    return {
        expiresAt: dto.expires_at,
        file: fromRawFileDTO(dto.raw_file),
        uploadUrl: dto.upload_url,
        progress: 0
    }
};

/**
 * uploads a raw file to the endpoint specified in the file object. While our
 * Neurotune Service returns an updated IRawFileDTO  object from the endpoint
 * we can't assume this as the url could point to S3 or something instead.
 * @param companyId
 * @param file
 * @param localFile the local file to upload
 * @param onUploadProgress
 */
export const uploadRawFile = async (
    _companyId: TCompanyId,
    file: IFileUploadStatus,
    localFile: FileWithPath,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
    config?: RetryConfig,
): Promise<IRawFile> => {
    // apiClient auto-adds /api to the path for us which can double add
    // it so we need to remove the api/ prefix if present
    const url = file.uploadUrl.replace("/api/", "/")
    const result = await apiClient(
        {
            method: "PUT",
            url,
            headers: {
                ...window.BIOS.headers,
                'Content-Type': 'application/octet-stream',
            },
            data: localFile,
            onUploadProgress,
            // Override the 10 s default: 1+ GB uploads legitimately take a
            // long time. A hung PUT is handled by the upload failure/retry
            // path, not by a timeout.
            timeout: UPLOAD_TIMEOUT_MS,
            ...config,
        })
    return fromRawFileDTO(result.data.data)
}

export const fromRawFileDTO = (dto: IRawFileDTO): IRawFile => {
    return {
        id: dto.id,
        subjectId: dto.subject_id,
        deviceId: dto.device_id,
        storageType: dto.storage_type,
        uploadStatus: dto.upload_status,
        filename: dto.filename,
        fileSuffix: dto.file_suffix,
        metadata: dto.metadata,
        size: dto.size,
        startTime: dto.start_time,
        endTime: dto.end_time,
        tags: dto.tags || [],
        createdAt: dto.created_at,
        uploadedAt: dto.uploaded_at,
        expiresAt: dto.expires_at,
    }
}